import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the course slice
const initialState = {
  courseData: {
    data: [],
  },
  status: "idle",
  locationId: "",
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list of course.
 * @returns {Array} - List of course.
 */
export const courseList = createAsyncThunk(
  "listCourse",
  async ({ locationId, pageCount, navigate, publish }, { rejectWithValue }) => {
    try {
      const data = await httpRequest(
        {
          method: "GET",
          url: `admin/courses/completions?location_id=${locationId}&page=${pageCount}`,
        },
        navigate
      );
      return {
        data,
        locationId,
        publish,
      };
    } catch (err) {
      return rejectWithValue(err.message || messages.listCourse.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for getting Fixture by course ID.
 * @param {string} id - The ID of the course.
 * @returns {Array} - List of Fixture.
 */
export const getCompletionsList = createAsyncThunk(
  "completionsList",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/courses/${id}/completions`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.completionsList.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for getting Fixture by course ID.
 * @param {string} id - The ID of the course.
 * @returns {Array} - List of Fixture.
 */
export const getCompletionsExport = createAsyncThunk(
  "completionsExport",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      // Fetch the blob from the API
      const blob = await httpRequest(
        {
          method: "GET",
          url: `admin/courses/${id}/completions/export`,
          responseType: "blob", // Ensure the response is of type Blob
        },
        navigate
      );

      // Ensure the response is a valid Blob
      if (!blob || blob.size === 0) {
        throw new Error("No content in the exported file.");
      }

      // Create a URL from the Blob for the download
      const url = URL.createObjectURL(blob);
      return { id, url }; // Return the URL for download
    } catch (err) {
      console.error("Error in getCompletionsExport:", err);
      return rejectWithValue(err.message || "Error exporting completions", {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for course-related actions and state management
const courseCompletionSlice = createSlice({
  name: "coursePoint",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
    setClearCourseData: (state, action) => {
      state.courseData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [getCompletionsList.pending].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [getCompletionsList.fulfilled].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";

          if (action.type === "listCourse/fulfilled") {
            // August
            if (action.payload.publish === "publish") {
              state.courseData = {
                data: [],
              };
            }

            if (state.locationId !== action.payload.locationId) {
              state.courseData = {
                data: [],
              };
            }

            state.locationId = action.payload.locationId;

            // const existingData = state.zoneData.data;
            // const newData = action.payload.data.zones.data;

            const existingData = Array.isArray(state.courseData.data)
              ? state.courseData.data
              : [];

            const newData = Array.isArray(action.payload.data?.courses?.data)
              ? action.payload.data.courses.data
              : [];

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.courseData = {
              ...action.payload.data.courses,
              data: mergedData,
            };
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete Assembly
          if (
            [
              // "printingCourse/fulfilled",
            ].includes(action.type)
          ) {
            courseCompletionSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]]?.success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [getCompletionsList.rejected].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);
          courseCompletionSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

// Action creators are generated for each case reducer function
export const { setClearCourseData } = courseCompletionSlice.actions;

export default courseCompletionSlice.reducer;